import { Children, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "../Theming/ThemingProvider";
import "./HpTabs.css";

// Marker component — rendering is handled by HpTabs
export const HpTab = () => null;

export const HpTabs = ({
  value,
  onChange,
  children,
  primaryColor,
  className = "",
  style,
  align,             // "start" → left-align; default → centered
  fullWidthIndicator = false, // true → border spans full row width
}) => {
  const { theme } = useTheme();
  const primary = primaryColor || theme.primaryColor;

  const listRef = useRef(null);
  const underlineRef = useRef(null);
  const tabRefs = useRef({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const tabs = Children.toArray(children).filter((c) => c.type === HpTab);

  // offsetLeft is relative to hp-tabs__tabs-inner (position:relative)
  const updateUnderline = useCallback(() => {
    const key = String(value);
    const tabEl = tabRefs.current[key];
    const underlineEl = underlineRef.current;
    if (!tabEl || !underlineEl) return;
    underlineEl.style.left = tabEl.offsetLeft + "px";
    underlineEl.style.width = tabEl.offsetWidth + "px";
  }, [value]);

  const updateScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useLayoutEffect(() => {
    updateUnderline();
  }, [updateUnderline]);

  useEffect(() => {
    const key = String(value);
    const tabEl = tabRefs.current[key];
    tabEl?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [value]);

  useEffect(() => {
    updateScroll();
    const el = listRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScroll, { passive: true });
    const ro = new ResizeObserver(() => {
      updateScroll();
      updateUnderline();
    });
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScroll);
      ro.disconnect();
    };
  }, [updateScroll, updateUnderline]);

  const scrollBy = (dir) =>
    listRef.current?.scrollBy({ left: dir * 160, behavior: "smooth" });

  const innerClass = [
    "hp-tabs__tabs-inner",
    align === "start" ? "hp-tabs__tabs-inner--start" : "",
    fullWidthIndicator ? "hp-tabs__tabs-inner--full" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`hp-tabs ${className}`} style={{ "--tabs-primary": primary, ...style }}>
      <div className="hp-tabs__wrap">
        <button
          type="button"
          className={`hp-tabs__arrow hp-tabs__arrow--left ${canScrollLeft ? "hp-tabs__arrow--visible" : ""}`}
          onClick={() => scrollBy(-1)}
          aria-label="Scroll left"
          tabIndex={canScrollLeft ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className={`hp-tabs__scroll-area ${fullWidthIndicator ? "hp-tabs__scroll-area--full-indicator" : ""}`}>
          <div className={`hp-tabs__shadow hp-tabs__shadow--left ${canScrollLeft ? "hp-tabs__shadow--visible" : ""}`} />

          <div className="hp-tabs__list" ref={listRef}>
            <div className={innerClass}>
              {tabs.map((tab) => {
                const { value: tabValue, label, children: tabChildren } = tab.props;
                const key = String(tabValue);
                const isActive = tabValue === value;
                return (
                  <button
                    key={key}
                    type="button"
                    ref={(el) => { if (el) tabRefs.current[key] = el; }}
                    className={`hp-tabs__tab${isActive ? " hp-tabs__tab--active" : ""}`}
                    onClick={() => onChange(tabValue)}
                  >
                    {label ?? tabChildren}
                  </button>
                );
              })}
              <div className="hp-tabs__underline" ref={underlineRef} />
            </div>
          </div>

          <div className={`hp-tabs__shadow hp-tabs__shadow--right ${canScrollRight ? "hp-tabs__shadow--visible" : ""}`} />
        </div>

        <button
          type="button"
          className={`hp-tabs__arrow hp-tabs__arrow--right ${canScrollRight ? "hp-tabs__arrow--visible" : ""}`}
          onClick={() => scrollBy(1)}
          aria-label="Scroll right"
          tabIndex={canScrollRight ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};
